import React from 'react';
import { X, Trophy, Star, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import '../styles/celebration.css';

interface CompletionCelebrationProps {
    totalSteps: number;
    buildTime?: string;
    productName: string;
    onClose: () => void;
    onBuildAnother?: () => void;
}

const CompletionCelebration: React.FC<CompletionCelebrationProps> = ({
    totalSteps,
    buildTime = '45 minutes',
    productName = 'Headphones',
    onClose,
    onBuildAnother
}) => {
    const confetti = (
        <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 50 }, (_, i) => (
                <div
                    key={i}
                    className="confetti"
                    style={{
                        left: `${Math.random() * 100}%`,
                        animationDelay: `${Math.random() * 3}s`,
                        animationDuration: `${3 + Math.random() * 2}s`,
                        backgroundColor: ['#FF784D', '#FFB511', '#4E754F', '#4954D2', '#FF4949'][Math.floor(Math.random() * 5)]
                    }}
                />
            ))}
        </div>
    );

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
            {confetti}
            <motion.div
                initial={{ scale: 0.85, opacity: 0, y: 24 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 26, delay: 0.1 }}
                className="bg-white rounded-3xl w-full max-w-sm md:w-[380px] relative overflow-hidden shadow-2xl z-10"
            >
                <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-background rounded-full transition-colors z-10">
                    <X className="w-5 h-5 text-gray-400" />
                </button>
                <div className="p-8 pt-10 text-center">
                    <motion.div
                        initial={{ scale: 0, rotate: -20 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 18, delay: 0.25 }}
                        className="w-20 h-20 bg-gradient-to-br from-orange to-specialYellow rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg"
                    >
                        <Trophy className="w-10 h-10 text-white" />
                    </motion.div>
                    <h1 className="font-headline text-black mb-1">Build Complete!</h1>
                    <h2 className="font-headline text-kibuBlue mb-6">Master Builder</h2>
                    <div className="bg-background rounded-2xl p-5 mb-5">
                        <div className="grid grid-cols-2 gap-4 text-center">
                            <div>
                                <div className="flex items-center justify-center mb-1">
                                    <Sparkles className="w-4 h-4 text-green mr-1.5" />
                                    <span className="text-2xl font-headline text-black">{totalSteps}</span>
                                </div>
                                <p className="text-xs font-body text-gray-500">Steps Completed</p>
                            </div>
                            <div>
                                <div className="flex items-center justify-center mb-1">
                                    <Star className="w-4 h-4 text-specialYellow mr-1.5" />
                                    <span className="text-2xl font-headline text-black">100%</span>
                                </div>
                                <p className="text-xs font-body text-gray-500">Build Complete</p>
                            </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-grey">
                            <p className="text-sm font-body text-gray-500">
                                {productName} build time: <span className="font-medium text-black">{buildTime}</span>
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onBuildAnother || onClose}
                        className="w-full bg-orange hover:bg-orange/90 text-white font-body font-medium py-3 px-6 rounded-full transition-all hover:shadow-button-hover"
                    >
                        Build Another
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default CompletionCelebration;
